//
// Fit-to-content geometry for the floor plan renderers (FloorCanvas in the
// native Table Service surface, VisualTableSelector in hideHeader mode).
//
// Every floor plan is authored in a fixed 800x500 "stage" (the layout
// editor's canvas). Rendering that whole stage scaled-to-fit leaves most of
// the box empty whenever the tables only occupy part of it — tenant_demo's
// Dine In uses roughly the top-left 55% x 48%. This helper instead takes the
// bounding box of the markers themselves, scales it uniformly to fill the box
// (upscaling allowed, capped so a 2-table room doesn't become a giant circle)
// and centres it. The stage keeps its 800x500 coordinate space — callers only
// change the element's transform, so tap→popover math (client coords against
// the box rect) and the merge connectors drawn inside the stage are untouched.
export const STAGE_WIDTH = 800;
export const STAGE_HEIGHT = 500;

const DEFAULT_SIZE = 60;
const STAGE_BBOX = { x0: 0, y0: 0, x1: STAGE_WIDTH, y1: STAGE_HEIGHT };

export function fitFloorToBox(tables, boxW, boxH, { pad = 28, maxScale = 1.9 } = {}) {
    const w = Number(boxW) || 0;
    const h = Number(boxH) || 0;

    // Not laid out yet (jsdom, first paint) — identity until the caller
    // measures a real box, so nothing jumps off-screen meanwhile.
    if (w <= 0 || h <= 0) {
        return { scale: 1, offsetX: 0, offsetY: 0, bbox: { ...STAGE_BBOX } };
    }

    const list = (Array.isArray(tables) ? tables : []).filter(
        (t) => t && Number.isFinite(t.x) && Number.isFinite(t.y)
    );

    // No markers — behave exactly like the old min(w/800, h/500, 1) fit.
    if (list.length === 0) {
        const scale = Math.min(w / STAGE_WIDTH, h / STAGE_HEIGHT, 1);
        return {
            scale,
            offsetX: (w - STAGE_WIDTH * scale) / 2,
            offsetY: (h - STAGE_HEIGHT * scale) / 2,
            bbox: { ...STAGE_BBOX },
        };
    }

    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    list.forEach((t) => {
        const half = (Number(t.size) || DEFAULT_SIZE) / 2;
        x0 = Math.min(x0, t.x - half);
        x1 = Math.max(x1, t.x + half);
        y0 = Math.min(y0, t.y - half);
        y1 = Math.max(y1, t.y + half);
    });
    x0 -= pad; y0 -= pad; x1 += pad; y1 += pad;

    const bw = x1 - x0;
    const bh = y1 - y0;
    const scale = Math.min(w / bw, h / bh, maxScale);
    return {
        scale,
        offsetX: (w - bw * scale) / 2,
        offsetY: (h - bh * scale) / 2,
        bbox: { x0, y0, x1, y1 },
    };
}

// CSS transform for a stage element with `transform-origin: 0 0`.
export const floorTransform = ({ scale, offsetX, offsetY, bbox }) =>
    `translate(${offsetX - bbox.x0 * scale}px, ${offsetY - bbox.y0 * scale}px) scale(${scale})`;
